import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import {
  checkBulkDealStatus,
} from '../services/dealService';
import { AuthContext } from './AuthContext';

interface DealDataState {
  savedStatus: Record<string, boolean>;
  voteStatus: Record<string, number>;
  loading: boolean;
  error: string | null;
}

interface DealDataContextType extends DealDataState {
  loadDealData: (dealIds: string[]) => Promise<void>;
  getSavedStatus: (dealId: string) => boolean | undefined;
  getVoteStatus: (dealId: string) => number | undefined;
  updateSavedStatus: (dealId: string, saved: boolean) => void;
  updateVoteStatus: (dealId: string, voteValue: number) => void;
  clearData: () => void;
}

const DealDataContext = createContext<DealDataContextType | undefined>(undefined);

export const DealDataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useContext(AuthContext);
  const [state, setState] = useState<DealDataState>({
    savedStatus: {},
    voteStatus: {},
    loading: false,
    error: null,
  });

  // Track which deals have been loaded to prevent duplicate requests
  const loadedDeals = useRef<Set<string>>(new Set());
  const loadingPromise = useRef<Promise<void> | null>(null);
  // Track pending deal IDs that need to be loaded when auth becomes available
  const pendingDealIds = useRef<Set<string>>(new Set());

  const loadDealData = useCallback(
    async (dealIds: string[]) => {
      if (dealIds.length === 0) {
        return;
      }

      // If not authenticated, add to pending queue
      if (!isAuthenticated) {
        dealIds.forEach((id) => pendingDealIds.current.add(id));
        return;
      }

      // Filter out deals that are already loaded
      const newDealIds = dealIds.filter((id) => !loadedDeals.current.has(id));

      if (newDealIds.length === 0) {
        return; // All deals already loaded
      }

      // If there's already a loading operation in progress, wait for it
      if (loadingPromise.current) {
        await loadingPromise.current;
        return;
      }

      setState((prev) => ({ ...prev, loading: true, error: null }));

      loadingPromise.current = (async () => {
        try {
          // Make single combined API call instead of two separate calls
          const statusData = await checkBulkDealStatus(newDealIds);

          setState((prev) => ({
            ...prev,
            savedStatus: { ...prev.savedStatus, ...statusData.savedStatus },
            voteStatus: { ...prev.voteStatus, ...statusData.voteStatus },
            loading: false,
            error: null,
          }));

          // Mark these deals as loaded
          newDealIds.forEach((id) => loadedDeals.current.add(id));
        } catch (error) {
          setState((prev) => ({
            ...prev,
            loading: false,
            error: error instanceof Error ? error.message : 'Failed to load deal data',
          }));
        }
      })();

      await loadingPromise.current;
      loadingPromise.current = null;
    },
    [isAuthenticated]
  );

  // Process pending deals when authentication becomes available
  React.useEffect(() => {
    if (isAuthenticated && pendingDealIds.current.size > 0) {
      const pendingIds = Array.from(pendingDealIds.current);
      pendingDealIds.current.clear();
      loadDealData(pendingIds);
    }
  }, [isAuthenticated, loadDealData]);

  const getSavedStatus = useCallback(
    (dealId: string) => {
      return state.savedStatus[dealId];
    },
    [state.savedStatus]
  );

  const getVoteStatus = useCallback(
    (dealId: string) => {
      return state.voteStatus[dealId];
    },
    [state.voteStatus]
  );

  const updateSavedStatus = useCallback((dealId: string, saved: boolean) => {
    setState((prev) => ({
      ...prev,
      savedStatus: { ...prev.savedStatus, [dealId]: saved },
    }));
  }, []);

  const updateVoteStatus = useCallback((dealId: string, voteValue: number) => {
    setState((prev) => ({
      ...prev,
      voteStatus: { ...prev.voteStatus, [dealId]: voteValue },
    }));
  }, []);

  const clearData = useCallback(() => {
    setState({
      savedStatus: {},
      voteStatus: {},
      loading: false,
      error: null,
    });
    loadedDeals.current.clear();
  }, []);

  // Clear data when user logs out
  React.useEffect(() => {
    if (!isAuthenticated) {
      clearData();
      pendingDealIds.current.clear();
    }
  }, [isAuthenticated, clearData]);

  const contextValue = React.useMemo(
    () => ({
      ...state,
      loadDealData,
      getSavedStatus,
      getVoteStatus,
      updateSavedStatus,
      updateVoteStatus,
      clearData,
    }),
    [
      state,
      loadDealData,
      getSavedStatus,
      getVoteStatus,
      updateSavedStatus,
      updateVoteStatus,
      clearData,
    ]
  );

  return <DealDataContext.Provider value={contextValue}>{children}</DealDataContext.Provider>;
};

export const useDealData = () => {
  const context = useContext(DealDataContext);
  if (context === undefined) {
    throw new Error('useDealData must be used within a DealDataProvider');
  }
  return context;
};
